import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Col,
  Row,
  TextField,
  Select,
  Checkbox,
  Pane,
  PaneMenu,
  Paneset,
  IconButton,
} from '@folio/stripes/components';

import { LOGIN_OPTIONS, TLS_OPTIONS } from './constants';
import {
  validateRequired,
  validateEmail,
  validatePort,
  validateHost,
  composeValidators,
} from './validation';
import EmailTestModal from './EmailTestModal';

const EmailSettingsForm = ({
  initialValues,
  onSubmit,
  onTestEmail,
  isLoading,
  isTestLoading,
  stripes,
  hasError,
}) => {
  const intl = useIntl();
  const [showPassword, setShowPassword] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);

  const canEdit =
    stripes?.hasPerm?.('ui-tenant-settings.settings.email.view') || true;

  const loginOptions = LOGIN_OPTIONS.map((option) => ({
    value: option.value,
    label: intl.formatMessage({ id: option.labelId }),
  }));

  const tlsOptions = TLS_OPTIONS.map((option) => ({
    value: option.value,
    label: intl.formatMessage({ id: option.labelId }),
  }));

  const handleTestEmail = (testEmail) => {
    onTestEmail(testEmail);
    setShowTestModal(false);
  };

  const lastMenu = (
    <PaneMenu>
      {canEdit && (
        <IconButton
          id="test-email-button"
          icon="envelope"
          onClick={() => setShowTestModal(true)}
          title={intl.formatMessage({
            id: 'ui-tenant-settings.settings.emailSettings.testEmail',
          })}
          disabled={isTestLoading}
        />
      )}
    </PaneMenu>
  );

  return (
    <>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, pristine, submitting }) => (
          <Paneset>
            <Pane
              defaultWidth="fill"
              fluidContentWidth
              paneTitle={
                <FormattedMessage id="ui-tenant-settings.settings.emailSettings.label" />
              }
              lastMenu={lastMenu}
            >
              <form id="email-settings-form" onSubmit={handleSubmit}>
                {hasError && (
                  <div
                    style={{
                      padding: '1rem',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: '4px',
                      marginBottom: '1rem',
                    }}
                  >
                    <strong>Note:</strong> mod-email module may not be
                    available. You can still configure settings, but they may
                    not be saved until the module is installed.
                  </div>
                )}
                <Row>
                  <Col xs={12} md={6}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.hostName" />
                      }
                      name="host"
                      id="host"
                      component={TextField}
                      fullWidth
                      required
                      disabled={!canEdit}
                      validate={composeValidators(
                        validateRequired,
                        validateHost,
                      )}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.portNumber" />
                      }
                      name="port"
                      id="port"
                      component={TextField}
                      type="number"
                      fullWidth
                      required
                      disabled={!canEdit}
                      validate={composeValidators(
                        validateRequired,
                        validatePort,
                      )}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.userName" />
                      }
                      name="username"
                      id="username"
                      component={TextField}
                      fullWidth
                      required
                      disabled={!canEdit}
                      validate={validateRequired}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <div style={{ position: 'relative' }}>
                      <Field
                        label={
                          <FormattedMessage id="ui-tenant-settings.settings.emailSettings.password" />
                        }
                        name="password"
                        id="password"
                        component={TextField}
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        required
                        disabled={!canEdit}
                        validate={validateRequired}
                      />
                      {canEdit && (
                        <Button
                          type="button"
                          buttonStyle="link"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '30px',
                          }}
                        >
                          {showPassword ? 'Hide' : 'Show'}
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.emailSender" />
                      }
                      name="from"
                      id="from"
                      component={TextField}
                      fullWidth
                      required
                      disabled={!canEdit}
                      validate={composeValidators(
                        validateRequired,
                        validateEmail,
                      )}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.loginOptions" />
                      }
                      name="loginOption"
                      id="loginOption"
                      component={Select}
                      dataOptions={loginOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.TLSOptions" />
                      }
                      name="startTlsOptions"
                      id="startTlsOptions"
                      component={Select}
                      dataOptions={tlsOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.authMethods" />
                      }
                      name="authMethods"
                      id="authMethods"
                      component={TextField}
                      fullWidth
                      disabled={!canEdit}
                      placeholder="CRAM-MD5 LOGIN PLAIN"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={4}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.SSLOptions" />
                      }
                      name="ssl"
                      id="ssl"
                      component={Checkbox}
                      type="checkbox"
                      disabled={!canEdit}
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <Field
                      label={
                        <FormattedMessage id="ui-tenant-settings.settings.emailSettings.trustAll" />
                      }
                      name="trustAll"
                      id="trustAll"
                      component={Checkbox}
                      type="checkbox"
                      disabled={!canEdit}
                    />
                  </Col>
                </Row>

                {canEdit && (
                  <Row>
                    <Col xs={12}>
                      <Button
                        id="save-email-settings"
                        type="submit"
                        buttonStyle="primary"
                        disabled={pristine || submitting || isLoading}
                        marginBottom0
                      >
                        <FormattedMessage id="ui-tenant-settings.settings.general.saveAndClose" />
                      </Button>
                    </Col>
                  </Row>
                )}
              </form>
            </Pane>
          </Paneset>
        )}
      />

      <EmailTestModal
        open={showTestModal}
        onConfirm={handleTestEmail}
        onCancel={() => setShowTestModal(false)}
        isLoading={isTestLoading}
      />
    </>
  );
};

EmailSettingsForm.propTypes = {
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onTestEmail: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isTestLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  stripes: PropTypes.shape({
    hasPerm: PropTypes.func,
  }),
};

export default EmailSettingsForm;
